/**
 * ZTYLIST — Animations & Interactions
 * assets/animations.js
 * CSS-first, performant, respects prefers-reduced-motion
 */
(function() {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── SCROLL REVEAL ──────────────────────────────────────────
  function initScrollReveal() {
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach(function(el) {
        el.classList.add('visible');
      });
      return;
    }

    var revealEls = document.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    if (!revealEls.length || !window.IntersectionObserver) {
      revealEls.forEach(function(el) { el.classList.add('visible'); });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px'
    });

    revealEls.forEach(function(el) { observer.observe(el); });
  }

  // ── MAGNETIC BUTTONS ───────────────────────────────────────
  function initMagneticButtons() {
    if (prefersReducedMotion) return;

    document.querySelectorAll('.btn--primary, .btn--cta').forEach(function(btn) {
      btn.addEventListener('mousemove', function(e) {
        var rect = this.getBoundingClientRect();
        var x = e.clientX - rect.left - rect.width  / 2;
        var y = e.clientY - rect.top  - rect.height / 2;
        var strength = 0.25;
        this.style.transform = 'translate(' + (x * strength) + 'px, ' + (y * strength) + 'px)';
      });

      btn.addEventListener('mouseleave', function() {
        this.style.transform = '';
        this.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
        var self = this;
        setTimeout(function() { self.style.transition = ''; }, 400);
      });
    });
  }

  // ── NEON FLICKER on hover ──────────────────────────────────
  function initNeonFlicker() {
    if (prefersReducedMotion) return;

    document.querySelectorAll('.text-glow-cyan, .text-glow-purple').forEach(function(el) {
      el.addEventListener('mouseenter', function() {
        this.style.animation = 'neonFlicker 0.4s ease forwards';
      });
      el.addEventListener('animationend', function() {
        this.style.animation = '';
      });
    });
  }

  // ── PARALLAX on hero ───────────────────────────────────────
  function initParallax() {
    if (prefersReducedMotion) return;
    var hero = document.querySelector('.hero__media img');
    if (!hero) return;

    var ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          var scrollY = window.scrollY;
          var speed = 0.35;
          hero.style.transform = 'translateY(' + (scrollY * speed) + 'px) scale(1.1)';
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // NOTE: A per-card mousemove "card glow" used to live here. It rewrote
  // card.style.background on every pointer move (un-throttled), causing heavy
  // repaints across a whole product grid. Removed for performance — the cards
  // already have rich GPU-only hover effects (lift, border glow, image zoom)
  // defined in CSS.

  // ── CINEMATIC LOADING SEQUENCE ─────────────────────────────
  function initPageReveal() {
    if (prefersReducedMotion) return;

    var hero = document.querySelector('.hero');
    if (hero) {
      hero.style.opacity = '1';
    }
  }

  // ── COUNTER ANIMATION ──────────────────────────────────────
  function animateCounter(el) {
    if (prefersReducedMotion) return;
    var target = parseFloat(el.dataset.target || el.textContent);
    var suffix = el.dataset.suffix || '';
    var duration = 1200;
    var start = performance.now();

    function update(now) {
      var elapsed = now - start;
      var progress = Math.min(elapsed / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var value = Math.round(target * eased);
      el.textContent = value + suffix;
      if (progress < 1) requestAnimationFrame(update);
    }

    requestAnimationFrame(update);
  }

  function initCounters() {
    if (prefersReducedMotion || !window.IntersectionObserver) return;

    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach(function(el) { observer.observe(el); });
  }

  // ── SMOOTH SCROLL for anchor links ─────────────────────────
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function(a) {
      a.addEventListener('click', function(e) {
        var id = this.getAttribute('href').slice(1);
        if (!id) return;
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        var offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 72;
        var top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
        if (prefersReducedMotion) {
          window.scrollTo({ top: top });
        } else {
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      });
    });
  }

  // ── TOAST NOTIFICATIONS ────────────────────────────────────
  window.ZT = window.ZT || {};
  window.ZT.toast = function(message, type, duration) {
    var toast = document.getElementById('Toast');
    if (!toast) return;
    toast.textContent = message;
    toast.className = 'toast';
    if (type) toast.classList.add('toast--' + type);
    toast.classList.add('visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function() {
      toast.classList.remove('visible');
    }, duration || 3500);
  };

  // ── INIT ───────────────────────────────────────────────────
  function init() {
    initScrollReveal();
    initMagneticButtons();
    initNeonFlicker();
    initParallax();
    initPageReveal();
    initCounters();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run reveal on dynamic content
  document.addEventListener('zt:content:updated', initScrollReveal);

})();
